import { useCallback, useEffect, useState } from 'react';

import type { ScreenId } from '../components/layout/navigation';
import { screenFromPath, screenPath } from '../lib/routes';

export interface RouteController {
  screen: ScreenId;
  navigate: (screen: ScreenId) => void;
}

/**
 * Tela atual na barra de endereço, sem biblioteca de rotas: são nove telas sem
 * parâmetro nenhum, e o History API resolve.
 *
 * Antes disso a tela vivia só em `useState` e o endereço nunca mudava: F5 sempre
 * voltava para o dashboard, o botão "voltar" do navegador saía do app e não dava
 * para mandar o link de uma tela para alguém. O `replaceState` do efeito existe
 * para o endereço nunca contradizer o que está na tela — um caminho desconhecido
 * (ou um link antigo) mostra o dashboard e passa a dizer `/`.
 *
 * Depende do servidor devolver o index.html em qualquer caminho: o Vite faz isso
 * por padrão em desenvolvimento e o Nginx faz pelo `try_files` do nginx.conf.
 */
export function useRoute(): RouteController {
  const [screen, setScreen] = useState<ScreenId>(() =>
    screenFromPath(window.location.pathname)
  );

  // Voltar/avançar do navegador não passam pelo `navigate`.
  useEffect(() => {
    const onPopState = () => setScreen(screenFromPath(window.location.pathname));

    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  useEffect(() => {
    const canonical = screenPath(screen);

    if (window.location.pathname !== canonical) {
      window.history.replaceState(null, '', `${canonical}${window.location.search}`);
    }
  }, [screen]);

  const navigate = useCallback((next: ScreenId) => {
    // Lê o endereço em vez do estado para o clique repetido no item já aberto
    // não empilhar entradas idênticas no histórico.
    if (screenFromPath(window.location.pathname) === next) return;

    window.history.pushState(null, '', screenPath(next));
    setScreen(next);
  }, []);

  return { screen, navigate };
}
